import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
    Legend,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber, formatCurrency } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

export const BreakEvenDayChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const { chartData, breakEvenDate } = useMemo(() => {
        if (!finance?.dailyBreakdown) return { chartData: [], breakEvenDate: null as string | null };

        let cumulativeRevenue = 0;
        let cumulativeExpenses = 0;
        let foundBreakEven: string | null = null;
        let prevDiff = 0;

        const data = Object.entries(finance.dailyBreakdown)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, day]: [string, Record<string, unknown>]) => {
                const metrics = day.metrics as Record<string, number> | undefined;
                cumulativeRevenue += metrics?.revenue ?? 0;
                cumulativeExpenses += metrics?.expenses ?? 0;

                const diff = cumulativeRevenue - cumulativeExpenses;

                // Detect the first crossing from negative to positive
                if (!foundBreakEven && prevDiff < 0 && diff >= 0) {
                    foundBreakEven = date;
                }
                prevDiff = diff;

                return {
                    date,
                    cumulativeRevenue,
                    cumulativeExpenses,
                };
            });

        return { chartData: data, breakEvenDate: foundBreakEven };
    }, [finance?.dailyBreakdown]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({
        isEmpty,
        summary: !isEmpty && breakEvenDate ? (
            <span style={{ color: CHART_COLORS.revenue }}>
                {format(parseISO(breakEvenDate), 'MMM d')}
            </span>
        ) : !isEmpty ? (
            <span style={{ color: CHART_COLORS.expense }}>
                {t('charts.noBreakEven', 'Not reached')}
            </span>
        ) : undefined,
    });

    if (isEmpty) return null;

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={CHART_STYLES.margin.withAxis}>
                        <defs>
                            <linearGradient id="breakEvenRevenueGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={CHART_COLORS.revenue} stopOpacity={0.15} />
                                <stop offset="95%" stopColor={CHART_COLORS.revenue} stopOpacity={0.02} />
                            </linearGradient>
                            <linearGradient id="breakEvenExpenseGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={CHART_COLORS.expense} stopOpacity={0.15} />
                                <stop offset="95%" stopColor={CHART_COLORS.expense} stopOpacity={0.02} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="date"
                            tickFormatter={(v: string) => format(parseISO(v), 'MMM d')}
                            {...CHART_STYLES.axis}
                        />
                        <YAxis
                            tickFormatter={(v: number) => formatCompactNumber(v, currencySymbol)}
                            {...CHART_STYLES.axis}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            itemStyle={CHART_STYLES.tooltip.itemStyle}
                            formatter={(value: number, name: string) => [
                                formatCurrency(value, currencySymbol),
                                name,
                            ]}
                            labelFormatter={(label: string) => format(parseISO(label), 'MMM d, yyyy')}
                        />
                        <Legend wrapperStyle={{ fontSize: '10px' }} />
                        {breakEvenDate && (
                            <ReferenceLine
                                x={breakEvenDate}
                                stroke={CHART_COLORS.primary}
                                strokeDasharray="4 4"
                                strokeWidth={2}
                                label={{
                                    value: t('charts.breakEven', 'Break-even'),
                                    position: 'top',
                                    fill: CHART_COLORS.primary,
                                    fontSize: 10,
                                }}
                            />
                        )}
                        <Area
                            type="monotone"
                            dataKey="cumulativeRevenue"
                            name={t('charts.cumulativeRevenue', 'Cumulative Revenue')}
                            stroke={CHART_COLORS.revenue}
                            strokeWidth={CHART_STYLES.line.strokeWidth}
                            fill="url(#breakEvenRevenueGradient)"
                            dot={false}
                            activeDot={{ ...CHART_STYLES.line.activeDot, fill: CHART_COLORS.revenue }}
                        />
                        <Area
                            type="monotone"
                            dataKey="cumulativeExpenses"
                            name={t('charts.cumulativeExpenses', 'Cumulative Expenses')}
                            stroke={CHART_COLORS.expense}
                            strokeWidth={CHART_STYLES.line.strokeWidth}
                            fill="url(#breakEvenExpenseGradient)"
                            dot={false}
                            activeDot={{ ...CHART_STYLES.line.activeDot, fill: CHART_COLORS.expense }}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
